"use client";

/**
 * Goal-gradient onboarding card for fresh signed-in users: the first
 * steps for what they are doing here, with "account created"
 * pre-checked, so the meter NEVER starts at zero (20% feels like
 * momentum; 0% feels like standing still).
 *
 * Self-hides when: signed out, all steps done, or dismissed (localStorage).
 * State comes from one cheap GET /api/auth/onboarding (EXISTS probes).
 */
import { useEffect, useState } from "react";
import Link from "@/lib/localized-link";
import { useAuth } from "@/lib/auth-context";
import { useT } from "@/lib/locale-context";
import { fetchAPI } from "@/lib/api";
import { CheckCircle2, Circle, X, ChevronRight } from "lucide-react";
import { encodePrefill } from "@/lib/filter-presets";
import { usePersonal, type Intent } from "@/lib/use-personal";

interface Status {
  email_verified: boolean;
  has_analysis: boolean;
  has_saved: boolean;
  has_alert: boolean;
  has_property?: boolean;
  has_tracked?: boolean;
}

type StepKey = "account" | "verify" | "analysis" | "save" | "map" | "alert" | "deal_alert" | "home" | "track";

// The first steps follow what the person is doing (lib/use-personal):
// the map for a home seeker, deal alerts for an investor, their own
// home and listing for a seller, their listings for an agent.
const PLAN: Record<Intent | "none", StepKey[]> = {
  buy: ["account", "verify", "analysis", "save", "map", "alert"],
  rent: ["account", "verify", "save", "map", "alert", "analysis"],
  invest: ["account", "verify", "deal_alert", "analysis", "save", "home"],
  sell: ["account", "verify", "home", "track", "analysis"],
  rent_out: ["account", "verify", "home", "track", "analysis"],
  agent: ["account", "verify", "track", "analysis", "alert"],
  // An office or a garage gets no valuation: the alert, the saved ones,
  // the map.
  commercial: ["account", "verify", "alert", "save", "map"],
  garage: ["account", "verify", "alert", "save", "map"],
  none: ["account", "verify", "analysis", "save", "map", "alert", "home"],
};

const DISMISS_KEY = "menzil.onboarding.dismissed";

/** ``embedded``: inside the signed-in hub, which owns width and spacing. */
export default function OnboardingChecklist({ embedded = false }: { embedded?: boolean } = {}) {
  const t = useT();
  const { user } = useAuth();
  const { data: personal } = usePersonal();
  const [status, setStatus] = useState<Status | null>(null);
  const [dismissed, setDismissed] = useState(true); // assume hidden until read
  // "Opened the map" is a client-side fact (no DB row to probe) - the
  // map page stamps localStorage on visit.
  const [mapSeen, setMapSeen] = useState(false);
  useEffect(() => {
    try { setMapSeen(localStorage.getItem("menzil.onboard.map_seen") === "1"); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  useEffect(() => {
    if (!user || dismissed) return;
    let alive = true;
    fetchAPI<Status>("/auth/onboarding")
      .then((s) => { if (alive) setStatus(s); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user, dismissed]);

  if (!user || dismissed || !status) return null;

  const all: Record<StepKey, { done: boolean; labelKey: Parameters<typeof t>[0]; href: string }> = {
    account: { done: true, labelKey: "onboard.step.account", href: "/profile" },
    verify: { done: status.email_verified, labelKey: "onboard.step.verify", href: "/profile" },
    analysis: { done: status.has_analysis, labelKey: "onboard.step.analysis", href: "/analyze" },
    save: { done: status.has_saved, labelKey: "onboard.step.save", href: "/listings" },
    map: { done: mapSeen, labelKey: "onboard.step.map", href: "/map" },
    alert: { done: status.has_alert, labelKey: "onboard.step.alert", href: "/alerts/new" },
    deal_alert: {
      done: status.has_alert,
      labelKey: "onboard.step.deal_alert",
      href: `/alerts/new?prefill=${encodePrefill({ leased: false, deal_tiers: ["great"] })}`,
    },
    // Owning a home here (valued + tracked) is a strong retention hook
    // (properties drive return visits), so it earns a first-steps slot.
    home: { done: status.has_property === true, labelKey: "onboard.step.home", href: "/properties/new" },
    track: { done: status.has_tracked === true, labelKey: "onboard.step.track", href: "/listing-analytics" },
  };
  const plan = PLAN[personal?.intent.effective ?? "none"];
  const steps = plan.map((key) => ({ key, ...all[key] }));
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;
  const pct = Math.round((doneCount / steps.length) * 100);
  const next = steps.find((s) => !s.done);

  return (
    <section className={embedded ? "" : "max-w-3xl mx-auto px-4 sm:px-6 mt-6"}>
      <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-bold text-gray-900">
            {t("onboard.title")}
            <span className="ml-2 text-gray-500 tabular-nums">{pct}%</span>
          </p>
          <button
            type="button"
            onClick={() => {
              setDismissed(true);
              try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
            }}
            aria-label={t("onboard.dismiss")}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Meter - starts at 20% by construction. */}
        <div className="mt-2.5 h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gray-900 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>

        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {steps.map((s) => (
            <li key={s.key}>
              {s.done ? (
                <span className="flex items-center gap-2 text-[13px] text-gray-400 line-through decoration-gray-300">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" />
                  {t(s.labelKey)}
                </span>
              ) : (
                <Link
                  href={s.href}
                  className="group flex items-center gap-2 text-[13px] font-medium text-gray-700 underline-offset-2 hover:text-gray-900 hover:underline"
                >
                  <Circle className="w-4 h-4 shrink-0 text-gray-300 group-hover:text-gray-500" />
                  {t(s.labelKey)}
                  {next?.key === s.key && (
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  )}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
